import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
  Alert,
  AlertTitle,
  Box,
} from "@mui/material";
import { dialog_border_radius } from "styles/Dimensions";
import { Button } from "@verily-src/react-design-system";
import {
  AddUserRolesRequest,
  CreateUserRequest,
  EmailType,
  GetUserByEmailRequest,
  GetUsersRolesRequest,
  Role,
  SendEmailRequest,
  StudyRole,
  User,
} from "generated/studyauth/studyauth_pb";
import {
  getAddUserDialogTitle,
  getUserRoleDescription,
} from "components/UserRenderers";
import UserInfoForm, { UserInfoFormRef } from "components/UserInfoForm";
import { useAppSelector } from "redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { StudyAuthServiceApiClient } from "apiclient/StudyAuthServiceApiClient";
import { RpcError, StatusCode } from "grpc-web";
import UpdateReasonForm, {
  UpdateReasonFormRef,
} from "components/UpdateReasonForm";
import A11yDialogTitle from "components/A11yDialogTitle";
import { telemetry, userNotAuthenticated } from "core/TelemetryController";

export const devOpsNoOtherRoleAllowed =
  "This user has already been invited as a DevOps user. Study or site level roles cannot be assigned to them.";

interface InviteStudyUserDialogProps {
  /** Whether to open or close the dialog */
  open: boolean;
  /** Assign user the role specified in userRole */
  userRole: Role;
  registryId: string;
  onClose(): void;
  onSuccess(): void;
}

// The dialog box for inviting a study user.
const InviteStudyUserDialog: React.FC<InviteStudyUserDialogProps> = ({
  open,
  userRole,
  registryId,
  onClose,
  onSuccess,
}) => {
  const auth0Config = useAppSelector((state) => state.auth0Config);
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const testConfig = useAppSelector((state) => state.testConfig);

  const userInfoFormRef = useRef<UserInfoFormRef>(null);
  const updateReasonFormRef = useRef<UpdateReasonFormRef>(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [disableAddUserButton, setDisableAddUserButton] = useState(false);

  // Set initial state when the dialog is opened.
  useEffect(() => {
    if (open) {
      setErrorMessage("");
    }
  }, [open]);

  const validateInputs = useCallback(async () => {
    let isInputsValid = await userInfoFormRef.current!.validateInput(
      true /* focusOnError */
    );
    if (
      !updateReasonFormRef.current!.validateInput(
        isInputsValid /* focusOnError */
      )
    ) {
      isInputsValid = false;
    }

    return isInputsValid;
  }, []);

  const handleClickAddUserButton = useCallback(() => {
    if (isAuthenticated && auth0Config) {
      (async () => {
        try {
          if (testConfig.showInviteUserError) {
            throw new Error("failed to invite user");
          }

          setDisableAddUserButton(true);

          const isInputsValid = await validateInputs();
          if (!isInputsValid) {
            setDisableAddUserButton(false);
            return;
          }

          const user = userInfoFormRef.current!.getUserInfo();

          const updateReason = updateReasonFormRef.current!.getUpdateReason();

          const token = await getAccessTokenSilently({
            audience: auth0Config.audience,
          });

          const client = new StudyAuthServiceApiClient(
            auth0Config.audience!,
            token
          );

          // Check if the user already exists.
          let existingUser: User | undefined;
          try {
            const requst = new GetUserByEmailRequest().setEmail(
              user.getEmailAddress()
            );
            existingUser = await client.getUserByEmail(requst);
          } catch (error) {
            if (
              !(
                error instanceof RpcError && error.code === StatusCode.NOT_FOUND
              )
            ) {
              telemetry.logError(error);
              throw error;
            }
          }

          let userName: string;
          if (existingUser === undefined) {
            // Create the user if it does not exist.
            const request = new CreateUserRequest()
              .setUser(user)
              .setUpdateReason(updateReason);
            const newUser = await client.createUser(request);
            userName = newUser.getName();
          } else {
            if (existingUser.getIsSuperAdmin()) {
              setErrorMessage(devOpsNoOtherRoleAllowed);
              setDisableAddUserButton(false);
              return;
            }

            userName = existingUser.getName();

            const getRolesRequest = new GetUsersRolesRequest()
              .setUserNamesList([userName])
              .setWithStudyInfo(false);

            const getRolesReponse = await client.getUsersRoles(getRolesRequest);

            // Make sure that new user role do not conflict with existing ones.
            const userRolesList = getRolesReponse.getUserRolesList();
            if (userRolesList.length > 0) {
              for (let studyRole of userRolesList[0].getStudyRolesList()) {
                if (registryId === studyRole.getRegistryId()) {
                  if (studyRole.getRole() === userRole) {
                    setErrorMessage(
                      "This user has already been invited to this study in this role. " +
                        "Remove them from this role. Then re-invite them."
                    );
                  } else {
                    setErrorMessage(
                      "This user was invited to this study in a different role. " +
                        "Please remove them from that role. Then reassign them to the new role."
                    );
                  }

                  setDisableAddUserButton(false);
                  return;
                }
              }
            }
          }

          // Add user roles.
          const studyRoles = new Array<StudyRole>();
          studyRoles.push(
            new StudyRole().setRegistryId(registryId).setRole(userRole)
          );

          const addRolesRequest = new AddUserRolesRequest()
            .setUserName(userName)
            .setStudyRolesList(studyRoles)
            .setUpdateReason(updateReason);

          await client.addUserRoles(addRolesRequest);

          // Send invitation email for a new user
          if (existingUser === undefined) {
            const sendEmailRequest = new SendEmailRequest()
              .setName(userName)
              .setEmailType(EmailType.INVITATION)
              .setUserRole(userRole)
              .setUpdateReason(updateReason);

            await client.sendEmail(sendEmailRequest);
          }

          onSuccess();
        } catch (error) {
          telemetry.logError(error);
          setErrorMessage("Failed to invite the user. Please try again.");
        }

        setDisableAddUserButton(false);
      })();
    } else {
      telemetry.logError(new Error(userNotAuthenticated));
      setErrorMessage(
        "User is not authenticated. Please logout and login again."
      );
    }
  }, [
    auth0Config,
    getAccessTokenSilently,
    isAuthenticated,
    onSuccess,
    registryId,
    testConfig.showInviteUserError,
    userRole,
    validateInputs,
  ]);

  const theme = useTheme();
  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        style: { borderRadius: dialog_border_radius },
        sx: { width: "648px !important" },
      }}
    >
      <A11yDialogTitle sx={{ paddingTop: "24px" }}>
        <Typography variant="display6">
          {getAddUserDialogTitle(userRole)}
        </Typography>
      </A11yDialogTitle>
      <DialogContent sx={{ paddingBottom: "0px" }}>
        {errorMessage.length > 0 && (
          <Alert severity="error" sx={{ marginBottom: "12px" }}>
            <AlertTitle>
              <Typography
                variant="body1em"
                color={theme.palette.error.textOnBackground}
              >
                Invitation failed
              </Typography>
            </AlertTitle>
            <Typography
              variant="body2"
              color={theme.palette.error.textOnBackground}
            >
              {errorMessage}
            </Typography>
          </Alert>
        )}
        <Typography variant="body1" sx={{ padding: "4px 0px 16px 0px" }}>
          {getUserRoleDescription(userRole)}
        </Typography>
        <UserInfoForm
          ref={userInfoFormRef}
          verilyEmailRequired={
            userRole === Role.CDM || userRole === Role.SPONSOR_OPS
          }
          onInputChanged={() => {
            setErrorMessage("");
          }}
        />
        <Box sx={{ marginTop: "8px", marginBottom: "22px" }}>
          <UpdateReasonForm
            ref={updateReasonFormRef}
            predefinedReasons={[
              "Access required to perform job responsibilities",
              "Temporary access required to perform troubleshooting",
            ]}
            reasonSelectionLabel="Reason for adding user (required)"
            reasonRequiredMessage="Reason for adding user required"
            otherReasonHint="Please provide reason this user is being added"
            otherReasonRequiredMessage="Other reason for adding user required"
            onInputChanged={() => {
              setErrorMessage("");
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        style={{ justifyContent: "right" }}
        sx={{ padding: "16px 24px 24px 24px" }}
      >
        <Button
          label="Cancel"
          onClick={onClose}
          variant="outlined"
          sx={{
            marginRight: "4px",
          }}
          data-testid="cancel-button"
        />
        <Button
          label="Add user"
          variant="filled"
          onClick={handleClickAddUserButton}
          disabled={disableAddUserButton}
          data-testid="continue-button"
        />
      </DialogActions>
    </Dialog>
  );
};

export default InviteStudyUserDialog;
